import {jsx} from 'panel';
import {VNode} from 'snabbdom';
import isNumber from 'lodash/isNumber';

import {mpColors} from 'common/util/colors';
import {SvgIconEl} from 'common/components/svg-icon';
import type {BodyCellRenderContext} from 'common/components/data-table/types';
import {LIFT_TOOLTIP_COMPARE, LiftType} from 'common/report/util/lift/types';
import {formatMetricNumber} from 'common/util';
import {TooltipEl} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {LENGTH, SIZE, TextHighlightEl, THEME} from 'common/components/text-highlight';

import {FunnelTableRow} from './types';

// Warning: css selectors in this file are styled by mp-data-table
// TODO: remove this obscure linkage

type RenderArgs = {
  item: FunnelTableRow;
  context: BodyCellRenderContext;
};

export type StatSigClickHandler = (ev: MouseEvent, row: FunnelTableRow) => void;

export function getCellStatSigRenderer(onStatSigClick: StatSigClickHandler) {
  return function CellStatSig({item, context}: RenderArgs) {
    if (item.isControlGroup) {
      return (
        <>
          {!context.isComparison ? (
            <div sel='.statSig-svg-wrapper.control-group-icon'>
              <SvgIconEl
                attrs={{
                  icon: `status-light`,
                }}
              />
            </div>
          ) : null}
          <div sel='.statSig-value' class={{'is-comparison': context.isComparison}}>
            Control
          </div>
        </>
      );
    } else if (item.statSig) {
      const {confidence, status} = item.statSig;
      const isOverall = !confidence;

      return (
        <>
          {!context.isComparison ? (
            <div
              sel='.statSig-svg-wrapper'
              class={{'control-group-icon': isOverall}}
              on={{
                click: (ev) => onStatSigClick(ev, item),
              }}
            >
              {isOverall ? (
                <SvgIconEl
                  attrs={{
                    icon: `status-light`,
                  }}
                />
              ) : (
                <SvgIconEl
                  attrs={{
                    icon: `status-light`,
                    color: mpColors[status],
                  }}
                />
              )}
            </div>
          ) : null}
          <div sel='.statSig-value' class={{'is-comparison': context.isComparison}}>
            {isOverall ? `` : confidence}
          </div>
        </>
      );
    } else {
      return null;
    }
  };
}

export function liftCellRenderer(liftType: LiftType, labelTransform): (RenderArgs) => string | VNode {
  return ({item: row, context}: RenderArgs): string | VNode => {
    if (liftType && isNumber(row[context.liftField])) {
      // Baseline and compare to past share the same logic, overall is disabled for funnels steps chart
      const isNegative = row[context.liftField] < 0;
      // Have to fix it to two decimals and re-parse because of JS floating point number multiplication (otherwise, 2.99 * 100 becomes 299.99, etc)
      const percent = Number((100 * row[context.liftField]).toFixed(2));
      return (
        <>
          <div sel='.mp-table-cell'>
            {context.label}
            <TextHighlightEl
              sel='.mp-lift-pill'
              attrs={{
                text: `${isNegative ? `` : `+`}${formatMetricNumber(percent)}%`,
                theme: isNegative ? THEME.NEGATIVE : THEME.POSITIVE,
                size: SIZE.SMALL,
                length: LENGTH.THIN,
              }}
            />
            <TooltipEl
              attrs={{
                placement: Placement.Top,
                text: `${labelTransform(row, context.field)} ${LIFT_TOOLTIP_COMPARE} ${labelTransform(
                  row,
                  context.liftExtra,
                )}`,
              }}
            />
          </div>
        </>
      );
    }

    return labelTransform(row, context.field);
  };
}
